import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { API_BASE } from '../config';

/**
 * Family members for the logged-in user — persisted in MongoDB via /api/family-members.
 */
const FamilyMembersContext = createContext(null);

const MAX_FAMILY_MEMBERS = 4;

async function apiRequest(token, path, options = {}) {
  const headers = {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };

  const res = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = new Error(data.error || data.message || 'Request failed');
    err.status = res.status;
    throw err;
  }
  return data;
}

export function FamilyMembersProvider({ children }) {
  const token = useSelector((state) => state.auth.token);
  const [familyMembers, setFamilyMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchFamilyMembers = useCallback(async () => {
    if (!token) {
      setFamilyMembers([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const data = await apiRequest(token, '/api/family-members');
      setFamilyMembers(Array.isArray(data.members) ? data.members : []);
    } catch (e) {
      console.error('Error loading family members:', e);
      setError(e.message || 'Failed to load family members');
      setFamilyMembers([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchFamilyMembers();
  }, [fetchFamilyMembers]);

  const addFamilyMember = useCallback(
    async (member) => {
      if (!member?.name?.trim()) return false;
      if (!token) return false;
      if (familyMembers.length >= MAX_FAMILY_MEMBERS) return false;

      try {
        const data = await apiRequest(token, '/api/family-members', {
          method: 'POST',
          body: JSON.stringify({
            name: member.name,
            age: member.age,
            gender: member.gender,
            phone: member.phone,
            relationship: member.relationship,
          }),
        });

        if (data.member) {
          setFamilyMembers((prev) => [...prev, data.member]);
        } else {
          await fetchFamilyMembers();
        }
        return true;
      } catch (e) {
        console.error('Error adding family member:', e);
        setError(e.message || 'Failed to add family member');
        return false;
      }
    },
    [token, familyMembers.length, fetchFamilyMembers]
  );

  const updateFamilyMember = useCallback(
    async (id, updatedData) => {
      if (!token || !id) return false;

      try {
        const data = await apiRequest(token, `/api/family-members/${id}`, {
          method: 'PUT',
          body: JSON.stringify(updatedData),
        });

        if (data.member) {
          setFamilyMembers((prev) =>
            prev.map((m) => (m.id === id ? { ...m, ...data.member } : m))
          );
        } else {
          await fetchFamilyMembers();
        }
        return true;
      } catch (e) {
        console.error('Error updating family member:', e);
        setError(e.message || 'Failed to update family member');
        return false;
      }
    },
    [token, fetchFamilyMembers]
  );

  const removeFamilyMember = useCallback(
    async (id) => {
      if (!token || !id) return false;

      try {
        await apiRequest(token, `/api/family-members/${id}`, {
          method: 'DELETE',
        });
        setFamilyMembers((prev) => prev.filter((m) => m.id !== id));
        return true;
      } catch (e) {
        console.error('Error removing family member:', e);
        setError(e.message || 'Failed to remove family member');
        return false;
      }
    },
    [token]
  );

  const clearFamilyMembers = useCallback(() => {
    setFamilyMembers([]);
    setError(null);
  }, []);

  const value = {
    familyMembers,
    loading,
    error,
    addFamilyMember,
    updateFamilyMember,
    removeFamilyMember,
    clearFamilyMembers,
    refreshFamilyMembers: fetchFamilyMembers,
    count: familyMembers.length,
    maxFamilyMembers: MAX_FAMILY_MEMBERS,
  };

  return (
    <FamilyMembersContext.Provider value={value}>
      {children}
    </FamilyMembersContext.Provider>
  );
}

export function useFamilyMembers() {
  const ctx = useContext(FamilyMembersContext);
  if (!ctx) throw new Error('useFamilyMembers must be used within FamilyMembersProvider');
  return ctx;
}
